import { isAnalyticsAdCreativeId } from "../../domain/ad-placements";
import type { AdCreative } from "../../domain/ad-placements";
import { RESULT_TYPE_KEYS, type ResultTypeKey } from "../../domain/results";
import { type ScoreBand } from "../../domain/scoring";

export const ANALYTICS_PAGE_PATHS = [
  "/",
  "/check",
  "/result",
  "/result/shared",
  "/privacy",
  "/terms",
  "/about",
] as const;

export type AnalyticsPagePath =
  (typeof ANALYTICS_PAGE_PATHS)[number] | "/r/:id";

export const ANALYTICS_ROUTE_KINDS = [
  "landing",
  "check",
  "result",
  "share_confirmation",
  "shared_result",
  "document",
  "content",
] as const;

export type AnalyticsRouteKind = (typeof ANALYTICS_ROUTE_KINDS)[number];

export const ANALYTICS_SHARE_CHANNELS = ["x", "line", "copy"] as const;
export type AnalyticsShareChannel = (typeof ANALYTICS_SHARE_CHANNELS)[number];

export const ANALYTICS_ENTRY_CHANNELS = [
  "direct",
  "search",
  "x",
  "line",
  "instagram",
  "tiktok",
  "other",
] as const;
export type AnalyticsEntryChannel = (typeof ANALYTICS_ENTRY_CHANNELS)[number];

export const ANALYTICS_PROVIDERS = ["a8"] as const;
export type AnalyticsProvider = (typeof ANALYTICS_PROVIDERS)[number];

export const ANALYTICS_PLACEMENTS = [
  "result_after_recovery",
  "shared_after_recovery",
] as const;
export type AnalyticsPlacement = (typeof ANALYTICS_PLACEMENTS)[number];

export type AnalyticsCreativeKey = AdCreative["id"];

export const ANALYTICS_CONTENT_SLUGS: readonly string[] = [];
export type AnalyticsContentSlug = string;

export const ANALYTICS_DEVICE_TYPES = [
  "desktop",
  "mobile",
  "tablet",
  "other",
] as const;
export type AnalyticsDeviceType = (typeof ANALYTICS_DEVICE_TYPES)[number];

export const ANALYTICS_BROWSER_FAMILIES = [
  "chrome",
  "safari",
  "firefox",
  "edge",
  "other",
] as const;
export type AnalyticsBrowserFamily =
  (typeof ANALYTICS_BROWSER_FAMILIES)[number];

export type AnalyticsAttribution = Readonly<{
  entry_channel?: AnalyticsEntryChannel;
  referring_domain?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}>;

export type AnalyticsEnvironment = Readonly<{
  device_type?: AnalyticsDeviceType;
  browser_family?: AnalyticsBrowserFamily;
  country_code?: string;
}>;

type SchemaVersion = Readonly<{ schema_version: 1 }>;
type ResultProperties = Readonly<{
  result_type: ResultTypeKey;
  score_band: ScoreBand;
}> &
  SchemaVersion;
type PageViewedProperties = Readonly<{
  route_kind: AnalyticsRouteKind;
  page_path: AnalyticsPagePath;
  content_slug?: AnalyticsContentSlug;
}> &
  AnalyticsAttribution &
  AnalyticsEnvironment &
  SchemaVersion;
type DiagnosisStartedProperties = AnalyticsAttribution &
  AnalyticsEnvironment &
  SchemaVersion;
type ShareActionProperties = Readonly<{
  share_channel: AnalyticsShareChannel;
  result_type: ResultTypeKey;
  score_band: ScoreBand;
}> &
  SchemaVersion;
type SharedResultProperties = ResultProperties &
  AnalyticsAttribution &
  AnalyticsEnvironment;
type MonetizationProperties = Readonly<{
  provider: AnalyticsProvider;
  placement: AnalyticsPlacement;
  creative_key: AnalyticsCreativeKey;
  content_slug?: AnalyticsContentSlug;
  result_type?: ResultTypeKey;
  score_band?: ScoreBand;
}> &
  SchemaVersion;
type ContentEngagedProperties = Readonly<{
  content_slug: AnalyticsContentSlug;
  engagement_seconds: 10 | 30;
}> &
  AnalyticsAttribution &
  SchemaVersion;
type ContentCtaProperties = Readonly<{
  content_slug: AnalyticsContentSlug;
  cta_kind: "diagnosis" | "related_content";
  cta_position: "intro" | "end";
}> &
  SchemaVersion;
type WebVitalProperties = Readonly<{
  route_kind: AnalyticsRouteKind;
  content_slug?: AnalyticsContentSlug;
  metric: "lcp" | "cls";
  value_bucket:
    | "lcp_good"
    | "lcp_needs_improvement"
    | "lcp_poor"
    | "cls_good"
    | "cls_needs_improvement"
    | "cls_poor";
}> &
  SchemaVersion;

export type AnalyticsEventMap = Readonly<{
  page_viewed: PageViewedProperties;
  diagnosis_started: DiagnosisStartedProperties;
  diagnosis_completed: ResultProperties;
  result_viewed: ResultProperties;
  share_action_selected: ShareActionProperties;
  share_created: ResultProperties;
  shared_result_viewed: SharedResultProperties;
  monetization_slot_viewed: MonetizationProperties;
  monetization_link_clicked: MonetizationProperties;
  content_engaged: ContentEngagedProperties;
  content_cta_selected: ContentCtaProperties;
  web_vital_measured: WebVitalProperties;
}>;

export type AnalyticsEventName = keyof AnalyticsEventMap;
export type AnalyticsEventPayload<K extends AnalyticsEventName> =
  AnalyticsEventMap[K];
export type SanitizedAnalyticsCapture<
  K extends AnalyticsEventName = AnalyticsEventName,
> = Readonly<{ event: K; properties: AnalyticsEventMap[K] }>;

type RecordValue = Readonly<Record<string, unknown>>;

const SCORE_BANDS: readonly ScoreBand[] = [
  "low",
  "signs",
  "progressing",
  "warning",
  "advanced",
];

const isOneOf = <T extends string>(
  value: unknown,
  allowed: readonly T[],
): value is T => typeof value === "string" && allowed.includes(value as T);

const asRecord = (value: unknown): RecordValue | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as RecordValue)
    : null;

const readSchemaVersion = (properties: RecordValue): SchemaVersion | null =>
  properties.schema_version === 1 ? { schema_version: 1 } : null;

const readResult = (
  properties: RecordValue,
): Omit<ResultProperties, "schema_version"> | null => {
  if (!isOneOf(properties.result_type, RESULT_TYPE_KEYS)) return null;
  if (!isOneOf(properties.score_band, SCORE_BANDS)) return null;
  return {
    result_type: properties.result_type,
    score_band: properties.score_band,
  };
};

const isContentSlug = (value: unknown): value is AnalyticsContentSlug =>
  typeof value === "string" && ANALYTICS_CONTENT_SLUGS.includes(value);

const normalizeUtm = (value: unknown): string | null => {
  if (typeof value !== "string") return null;

  let decoded = value;
  for (let attempt = 0; attempt < 2 && decoded.includes("%"); attempt += 1) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      return null;
    }
  }
  if (decoded.includes("%")) return null;
  if (
    decoded.includes("@") ||
    /^[a-z][a-z0-9+.-]*:/i.test(decoded) ||
    /(^|\/)\/\//.test(decoded) ||
    /(^|\/)www\./i.test(decoded)
  )
    return null;

  const normalized = decoded.toLowerCase().replace(/[^a-z0-9._-]/g, "");
  return normalized.length > 0 && normalized.length <= 64 ? normalized : null;
};

const readAttribution = (
  properties: RecordValue,
): AnalyticsAttribution | null => {
  const attribution: Record<string, string> = {};
  if (
    properties.entry_channel !== undefined &&
    !isOneOf(properties.entry_channel, ANALYTICS_ENTRY_CHANNELS)
  )
    return null;
  if (typeof properties.entry_channel === "string")
    attribution.entry_channel = properties.entry_channel;

  if (properties.referring_domain !== undefined) {
    if (
      typeof properties.referring_domain !== "string" ||
      !/^[a-z0-9.-]+$/i.test(properties.referring_domain) ||
      properties.referring_domain.length > 253
    )
      return null;
    attribution.referring_domain = properties.referring_domain.toLowerCase();
  }

  for (const key of ["utm_source", "utm_medium", "utm_campaign"] as const) {
    if (properties[key] === undefined) continue;
    const value = normalizeUtm(properties[key]);
    if (value === null) return null;
    attribution[key] = value;
  }
  return attribution;
};

const readEnvironment = (
  properties: RecordValue,
): AnalyticsEnvironment | null => {
  const environment: Record<string, string> = {};
  if (
    properties.device_type !== undefined &&
    !isOneOf(properties.device_type, ANALYTICS_DEVICE_TYPES)
  )
    return null;
  if (typeof properties.device_type === "string")
    environment.device_type = properties.device_type;

  if (
    properties.browser_family !== undefined &&
    !isOneOf(properties.browser_family, ANALYTICS_BROWSER_FAMILIES)
  )
    return null;
  if (typeof properties.browser_family === "string")
    environment.browser_family = properties.browser_family;

  if (properties.country_code !== undefined) {
    if (
      typeof properties.country_code !== "string" ||
      !/^[a-z]{2}$/i.test(properties.country_code)
    )
      return null;
    environment.country_code = properties.country_code.toUpperCase();
  }
  return environment;
};

const readContentSlug = (
  properties: RecordValue,
  required = false,
): Readonly<{ content_slug?: AnalyticsContentSlug }> | null => {
  if (properties.content_slug === undefined) return required ? null : {};
  return isContentSlug(properties.content_slug)
    ? { content_slug: properties.content_slug }
    : null;
};

export const normalizeAnalyticsPath = (
  rawPath: string,
): AnalyticsPagePath | null => {
  try {
    const pathname = new URL(rawPath, "https://analytics.invalid").pathname;
    if (/^\/r\/[^/]+$/.test(pathname)) return "/r/:id";
    return (ANALYTICS_PAGE_PATHS as readonly string[]).includes(pathname)
      ? (pathname as AnalyticsPagePath)
      : null;
  } catch {
    return null;
  }
};

const entryChannelFor = (hostname: string | null): AnalyticsEntryChannel => {
  if (!hostname) return "direct";
  if (
    ["google.com", "google.co.jp", "bing.com", "yahoo.co.jp"].some(
      (searchHost) =>
        hostname === searchHost || hostname.endsWith(`.${searchHost}`),
    )
  )
    return "search";
  if (/(^|\.)(x\.com|twitter\.com)$/.test(hostname)) return "x";
  if (/(^|\.)line\.me$/.test(hostname)) return "line";
  if (/(^|\.)instagram\.com$/.test(hostname)) return "instagram";
  if (/(^|\.)tiktok\.com$/.test(hostname)) return "tiktok";
  return "other";
};

export const readAnalyticsAttribution = (
  location: URL,
  referrer: string,
): AnalyticsAttribution => {
  const referringDomain = (() => {
    try {
      return referrer ? new URL(referrer).hostname.toLowerCase() : null;
    } catch {
      return null;
    }
  })();

  const attribution: Record<string, string> = {
    entry_channel: entryChannelFor(referringDomain),
  };
  if (referringDomain) attribution.referring_domain = referringDomain;
  for (const key of ["utm_source", "utm_medium", "utm_campaign"] as const) {
    const value = normalizeUtm(location.searchParams.get(key));
    if (value !== null) attribution[key] = value;
  }
  return attribution;
};

const sanitizePageViewed = (
  properties: RecordValue,
): PageViewedProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const attribution = readAttribution(properties);
  const environment = readEnvironment(properties);
  const content = readContentSlug(properties);
  const pagePath =
    typeof properties.page_path === "string"
      ? normalizeAnalyticsPath(properties.page_path)
      : null;
  if (
    !schemaVersion ||
    !attribution ||
    !environment ||
    !content ||
    !pagePath ||
    !isOneOf(properties.route_kind, ANALYTICS_ROUTE_KINDS)
  )
    return null;
  return {
    route_kind: properties.route_kind,
    page_path: pagePath,
    ...content,
    ...attribution,
    ...environment,
    ...schemaVersion,
  };
};

const sanitizeDiagnosisStarted = (
  properties: RecordValue,
): DiagnosisStartedProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const attribution = readAttribution(properties);
  const environment = readEnvironment(properties);
  return schemaVersion && attribution && environment
    ? { ...attribution, ...environment, ...schemaVersion }
    : null;
};

const sanitizeResult = (properties: RecordValue): ResultProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const result = readResult(properties);
  return schemaVersion && result ? { ...result, ...schemaVersion } : null;
};

const sanitizeShareAction = (
  properties: RecordValue,
): ShareActionProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const result = readResult(properties);
  if (
    !schemaVersion ||
    !result ||
    !isOneOf(properties.share_channel, ANALYTICS_SHARE_CHANNELS)
  )
    return null;
  return {
    share_channel: properties.share_channel,
    ...result,
    ...schemaVersion,
  };
};

const sanitizeSharedResult = (
  properties: RecordValue,
): SharedResultProperties | null => {
  const result = readResult(properties);
  const schemaVersion = readSchemaVersion(properties);
  const attribution = readAttribution(properties);
  const environment = readEnvironment(properties);
  return result && schemaVersion && attribution && environment
    ? { ...result, ...attribution, ...environment, ...schemaVersion }
    : null;
};

const sanitizeMonetization = (
  properties: RecordValue,
): MonetizationProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const content = readContentSlug(properties);
  if (
    !schemaVersion ||
    !content ||
    !isOneOf(properties.provider, ANALYTICS_PROVIDERS) ||
    !isOneOf(properties.placement, ANALYTICS_PLACEMENTS) ||
    !isAnalyticsAdCreativeId(properties.creative_key)
  )
    return null;

  const result =
    properties.result_type === undefined && properties.score_band === undefined
      ? {}
      : readResult(properties);
  if (!result) return null;
  return {
    provider: properties.provider,
    placement: properties.placement,
    creative_key: properties.creative_key,
    ...content,
    ...result,
    ...schemaVersion,
  };
};

const sanitizeContentEngaged = (
  properties: RecordValue,
): ContentEngagedProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const content = readContentSlug(properties, true);
  const attribution = readAttribution(properties);
  if (
    !schemaVersion ||
    !content?.content_slug ||
    !attribution ||
    (properties.engagement_seconds !== 10 &&
      properties.engagement_seconds !== 30)
  )
    return null;
  return {
    content_slug: content.content_slug,
    engagement_seconds: properties.engagement_seconds,
    ...attribution,
    ...schemaVersion,
  };
};

const sanitizeContentCta = (
  properties: RecordValue,
): ContentCtaProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const content = readContentSlug(properties, true);
  if (
    !schemaVersion ||
    !content?.content_slug ||
    !isOneOf(properties.cta_kind, ["diagnosis", "related_content"] as const) ||
    !isOneOf(properties.cta_position, ["intro", "end"] as const)
  )
    return null;
  return {
    content_slug: content.content_slug,
    cta_kind: properties.cta_kind,
    cta_position: properties.cta_position,
    ...schemaVersion,
  };
};

const sanitizeWebVital = (
  properties: RecordValue,
): WebVitalProperties | null => {
  const schemaVersion = readSchemaVersion(properties);
  const content = readContentSlug(properties);
  const metric = properties.metric;
  const valueBucket = properties.value_bucket;
  if (
    !schemaVersion ||
    !content ||
    !isOneOf(properties.route_kind, ANALYTICS_ROUTE_KINDS) ||
    !isOneOf(metric, ["lcp", "cls"] as const) ||
    !isOneOf(valueBucket, [
      "lcp_good",
      "lcp_needs_improvement",
      "lcp_poor",
      "cls_good",
      "cls_needs_improvement",
      "cls_poor",
    ] as const) ||
    !valueBucket.startsWith(`${metric}_`)
  )
    return null;
  return {
    route_kind: properties.route_kind,
    ...content,
    metric,
    value_bucket: valueBucket,
    ...schemaVersion,
  };
};

export const sanitizeAnalyticsCapture = (
  event: string,
  properties: unknown,
): SanitizedAnalyticsCapture | null => {
  const source = asRecord(properties);
  if (!source) return null;

  switch (event) {
    case "page_viewed": {
      const sanitized = sanitizePageViewed(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "diagnosis_started": {
      const sanitized = sanitizeDiagnosisStarted(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "diagnosis_completed":
    case "result_viewed":
    case "share_created": {
      const sanitized = sanitizeResult(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "share_action_selected": {
      const sanitized = sanitizeShareAction(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "shared_result_viewed": {
      const sanitized = sanitizeSharedResult(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "monetization_slot_viewed":
    case "monetization_link_clicked": {
      const sanitized = sanitizeMonetization(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "content_engaged": {
      const sanitized = sanitizeContentEngaged(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "content_cta_selected": {
      const sanitized = sanitizeContentCta(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    case "web_vital_measured": {
      const sanitized = sanitizeWebVital(source);
      return sanitized ? { event, properties: sanitized } : null;
    }
    default:
      return null;
  }
};
